/**
 * On-load Tailwind animations start immediately; content fully below the fold finishes off-screen.
 * - *-inview classes — reveal when scrolled into view.
 * - Base animate-slide-in-* — if fully below the viewport at measure time, *--defer + same reveal.
 */
const VARIANTS = [
  {
    base: 'animate-slide-in-bottom',
    inview: 'animate-slide-in-bottom-inview',
    defer: 'animate-slide-in-bottom--defer',
  },
  {
    base: 'animate-slide-in-left',
    inview: 'animate-slide-in-left-inview',
    defer: 'animate-slide-in-left--defer',
  },
  {
    base: 'animate-slide-in-right',
    inview: 'animate-slide-in-right-inview',
    defer: 'animate-slide-in-right--defer',
  },
];

const observed = new WeakSet();

function whenDocumentReady(fn) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fn, { once: true });
  } else {
    queueMicrotask(fn);
  }
}

function isFullyBelowViewport(el) {
  const vh = window.innerHeight || document.documentElement.clientHeight;
  return el.getBoundingClientRect().top >= vh - 1;
}

function markDeferredSlideIns() {
  VARIANTS.forEach(({ base, inview, defer }) => {
    document.querySelectorAll('.' + base).forEach((el) => {
      if (el.classList.contains(inview)) {
        return;
      }
      if (isFullyBelowViewport(el)) {
        el.classList.add(defer);
      }
    });
  });
}

function watchSelectorQuery() {
  return VARIANTS.map(
    ({ base, inview, defer }) =>
      `.${inview}:not(.is-inview), .${base}.${defer}:not(.is-inview)`,
  ).join(', ');
}

function reducedMotionSelectorQuery() {
  return VARIANTS.map(
    ({ base, inview, defer }) =>
      `.${inview}, .${base}.${defer}`,
  ).join(', ');
}

function createObserver() {
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-inview');
          io.unobserve(entry.target);
        }
      });
    },
    {
      root: null,
      rootMargin: '0px 0px -8% 0px',
      threshold: 0.08,
    },
  );
  return io;
}

let sharedIo = null;

function setupScrollInAnimations() {
  markDeferredSlideIns();

  if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) {
    document.querySelectorAll(reducedMotionSelectorQuery()).forEach((el) => {
      el.classList.add('is-inview');
    });
    return;
  }

  if (typeof IntersectionObserver === 'undefined') {
    document.querySelectorAll(reducedMotionSelectorQuery()).forEach((el) => {
      el.classList.add('is-inview');
    });
    return;
  }

  if (!sharedIo) {
    sharedIo = createObserver();
  }

  document.querySelectorAll(watchSelectorQuery()).forEach((el) => {
    if (observed.has(el)) {
      return;
    }
    observed.add(el);
    sharedIo.observe(el);
  });
}

whenDocumentReady(setupScrollInAnimations);
window.addEventListener('load', setupScrollInAnimations, { once: true });
